/**
 * RCC — Retail Compliance Center.
 *
 * FSR-first compliance workspace per modules digest §RCC. 14 surfaces on a
 * grid dashboard; each routes to a dedicated surface view.
 */

import { Link, Route, Routes } from 'react-router';
import { useConfig } from '@/contexts/ConfigContext';
import { Pill } from '@/components/primitives';
import { RCC_SURFACES, type RccSurfaceDef } from './routes';

function Dashboard(): JSX.Element {
  const { config, state } = useConfig();
  const openAlerts: number = 0; // TODO: wire to a consolidated counter once QC+OFAC+CIP endpoints return totals.

  const grouped: Record<RccSurfaceDef['category'], RccSurfaceDef[]> = {
    live: [],
    workflow: [],
    periodic: [],
    reference: [],
  };
  for (const s of RCC_SURFACES) grouped[s.category].push(s);

  return (
    <div>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-5)',
          marginBottom: 'var(--sp-8)',
          paddingBottom: 'var(--sp-5)',
          borderBottom: '1px solid var(--surface-border)',
        }}
      >
        <div>
          <h1 style={{ margin: 0 }}>Retail Compliance Center</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--ink-600)', fontSize: 13 }}>
            FSR-first compliance workspace — OFAC, SAR, CTR, CIP, Adverse Action, Death, Escheatment.
          </p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 'var(--sp-3)', alignItems: 'center' }}>
          <Pill variant={openAlerts > 0 ? 'err' : 'ok'}>
            {openAlerts} open alert{openAlerts === 1 ? '' : 's'}
          </Pill>
          {/* openAlerts is sourced from aggregated API counters once wired */}
          {state === 'initializing' && <Pill variant="warn">Config initializing</Pill>}
        </div>
      </header>

      {(Object.keys(grouped) as RccSurfaceDef['category'][]).map((cat) =>
        grouped[cat].length === 0 ? null : (
          <section key={cat} style={{ marginBottom: 'var(--sp-8)' }}>
            <h2
              style={{
                fontSize: 12,
                textTransform: 'uppercase',
                letterSpacing: '0.6px',
                color: 'var(--ink-500)',
                margin: '0 0 var(--sp-4)',
              }}
            >
              {CATEGORY_LABELS[cat]}
            </h2>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                gap: 'var(--sp-5)',
              }}
            >
              {grouped[cat].map((s) => (
                <Link
                  key={s.slug}
                  to={`/rcc/${s.slug}`}
                  style={{
                    textDecoration: 'none',
                    color: 'inherit',
                    padding: 'var(--sp-5)',
                    background: 'var(--surface-card)',
                    border: '1px solid var(--surface-border)',
                    borderRadius: 'var(--r-md)',
                    boxShadow: 'var(--sh-1)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--sp-3)',
                    minHeight: 120,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
                    <span
                      aria-hidden
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 'var(--r-sm)',
                        background: 'var(--ink-100)',
                        color: 'var(--pcb-blue)',
                        display: 'grid',
                        placeItems: 'center',
                      }}
                    >
                      <s.icon size={16} />
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        color: 'var(--ink-500)',
                        fontFamily: 'var(--font-mono)',
                      }}
                    >
                      #{s.number}
                    </span>
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink-900)' }}>{s.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-600)', flex: 1 }}>{s.blurb}</div>
                </Link>
              ))}
            </div>
          </section>
        ),
      )}

      <footer
        style={{
          marginTop: 'var(--sp-10)',
          paddingTop: 'var(--sp-5)',
          borderTop: '1px solid var(--surface-border)',
          fontSize: 11,
          color: 'var(--ink-500)',
          display: 'flex',
          gap: 'var(--sp-6)',
          flexWrap: 'wrap',
        }}
      >
        <span>Notification templates loaded: {config.notificationTemplates?.templates?.length ?? 0}</span>
        <span>SDB timeline rows: {config.sdbTimeline.length}</span>
        <span>Retention policies: {config.retentionPolicies.length}</span>
      </footer>
    </div>
  );
}

const CATEGORY_LABELS: Record<RccSurfaceDef['category'], string> = {
  live: 'Live queues',
  workflow: 'Workflow drafts',
  periodic: 'Periodic reports',
  reference: 'Reference + advisory',
};

export default function RCC(): JSX.Element {
  return (
    <Routes>
      <Route index element={<Dashboard />} />
      {RCC_SURFACES.map((s) => (
        <Route key={s.slug} path={s.slug} element={<s.Component />} />
      ))}
    </Routes>
  );
}
